/**
 * Domain types shared across the SPA.
 *
 * These mirror the backend v2 surface (docs/design/system-design.md sec 4-7).
 * Field names are the vendor-neutral canonical names from the catalog design.
 * Anything the API returns should conform to these; anything the UI sends up is
 * a subset (Create/Update partials).
 */

// Type-only: the realm password policy shape lives beside the generator that mirrors it.
import type { PasswordPolicy } from "@/lib/tempPassword";
// Type-only: the last-sign-in shape lives beside the helpers that render it (OP#1897).
import type { SignIn } from "@/lib/signIn";
// Type-only: the summary-tile scope shape lives beside the label rule (OP#1787).
import type { TileScope } from "@/lib/tileScope";

/* ------------------------------------------------------------------ *
 * Tenancy + identity  (the Org -> Site -> Device spine)
 * ------------------------------------------------------------------ */

/** Roles, ordered by privilege. Mirrors backend `read_only < site_operator <
 *  org_admin < msp_admin < platform_admin`. Kept as a const tuple so RoleGate can compare
 *  rank. ROLE SPLIT (2026-10-08): platform_admin is the only global role; msp_admin is
 *  confined to the customers in its org_ids (the server enforces both -- see
 *  docs/design/msp-platform-admin-split.md). */
export const ROLE_ORDER = [
  "read_only",
  "site_operator",
  "org_admin",
  "msp_admin",
  "platform_admin",
] as const;
export type Role = (typeof ROLE_ORDER)[number];

export interface Org {
  id: string;
  name: string;
  slug: string;
  /** Quarantine org holds un-claimed devices until mTLS enrollment (sec 4). */
  is_quarantine?: boolean;
}

export interface Site {
  id: string;
  org_id: string;
  name: string;
  /** Free-form location label; physical sites carry `site_id` on device rows. */
  location?: string | null;
}

/**
 * A per-scope role grant. A user may hold different roles at different scopes
 * (e.g. org_admin on org A, read_only on org B). `site_id === null` means the
 * grant applies org-wide. Mirrors `g.auth.roles`.
 */
export interface RoleGrant {
  role: Role;
  org_id: string;
  site_id: string | null;
}

/** Response shape of GET /api/v2/me — hydrates the SessionContext store. */
export interface Me {
  user: {
    id: string; // OIDC sub
    email: string;
    display_name: string;
  };
  orgs: Org[];
  sites: Site[];
  roles: RoleGrant[];
  /** True for the MSP tier and above (msp_admin, platform_admin): manages customers -- the
   *  All-customers portal and "New customer". Since 2026-10-08 this is NOT "global": an
   *  msp_admin's `orgs` are only the customers it was given. */
  is_msp_admin: boolean;
  /** True only for platform_admin, the ONE global role (2026-10-08). Optional so an older
   *  backend that predates the field reads as false (fail closed). */
  is_platform_admin?: boolean;
}

/* ------------------------------------------------------------------ *
 * Devices + clients  (the operational objects)
 * ------------------------------------------------------------------ */

export type HealthState = "healthy" | "warn" | "critical" | "unknown";
/** Richer device lifecycle status (separates reachable-but-degraded from offline). */
/**
 * Device lifecycle status.
 *
 * "dormant" is deliberately separate from "offline": it means REGISTERED BUT NEVER
 * CONTACTED (stock that has been added to the controller and not yet plugged in), where
 * "offline" means WAS in contact and is not now. Folding the two together makes the offline
 * count a mix of real outages and paperwork -- which is exactly what happened on 2026-09-10,
 * when an ECS2100 that had never been uCentral-managed sat at OFFLINE and read as an outage.
 */
export type DeviceStatus =
  | "online"
  | "degraded"
  | "offline"
  | "provisioning"
  | "dormant";

/**
 * Applied-state verification verdict for a device's config.
 *
 * Three values, not two, on purpose: "unknown" means WE COULD NOT CHECK and must never be
 * rendered as a pass. Collapsing it into "verified" would recreate the silent-divergence
 * defect the verifier exists to catch, in the UI instead of the agent.
 */
export type ConfigVerdict = "verified" | "diverged" | "unknown";

/**
 * WHAT a verdict covers. It must always be read together with the verdict:
 *   "change"   -- the change we just made was observed on the device
 *   "document" -- the device is running the document owgw holds, which says nothing about
 *                 any particular operator change
 * A tick rendered from the verdict alone cannot tell these apart, so the UI never gets one
 * without the other.
 */
export type ConfigCoverage = "change" | "document";

/** One row of GET /api/v2/config/verification — the fleet-wide config-status column. */
export interface DeviceConfigStatus {
  serial: string;
  verdict: ConfigVerdict;
  /** Machine-readable reason (services/config_verify REASON_*), e.g. "change_not_observable". */
  reason: string;
  coverage: ConfigCoverage | null;
  change_scope: string | null;
  /** First time this outcome was seen ("diverged since"). Null => never verified. */
  checked_at: string | null;
  /** Most recent confirmation of the same outcome. */
  last_checked_at: string | null;
  mismatch_count: number;
  /**
   * Staleness (services/config_verify.staleness). `stale` is true whenever the device is not
   * ONLINE: the verdict is then its last word, not live state, and renders neutral with its
   * age ("Last confirmed 1d ago (device offline)") instead of on the healthy ramp.
   */
  device_online?: boolean;
  stale?: boolean;
  stale_reason?: string | null;
  /** last_checked_at ?? checked_at, i.e. when this outcome was last confirmed. */
  last_confirmed_at?: string | null;
  /**
   * Config-compatibility (services/config_compat): true when site operations SKIP this device
   * ("Not managed") -- stock firmware that refuses every Meridian config, a streak of wholesale
   * rejections, or an operator's "Exclude from Meridian config". The verdict above stays the
   * honest one (e.g. diverged/device_rejected); these say it is excluded, and why.
   */
  config_excluded?: boolean;
  config_excluded_source?: ConfigCompatSource | null;
  config_excluded_label?: string | null;
  config_excluded_reason?: string | null;
}

/** Why a device is excluded from Meridian config (services/config_compat SOURCE_*). */
export type ConfigCompatSource = "manual" | "firmware" | "rejections";

/** A device's config-compatibility (GET /devices/{serial}/config/verification `config_compat`). */
export interface ConfigCompat {
  excluded: boolean;
  source: ConfigCompatSource | null;
  /** Chip text: "Needs firmware" / "Excluded by operator". */
  label: string | null;
  reason: string | null;
  since: string | null;
  /** The newest operator decision, if any. */
  manual: "excluded" | "included" | null;
  manual_by?: string | null;
  /** A site SSID push skipped this AP; it gets the current site SSIDs on its next push. */
  needs_resync: boolean;
}

/**
 * One hourly connectivity bucket.
 *   "up"  -- the device produced telemetry in that hour (evidence it was talking)
 *   "gap" -- silence BETWEEN two confirmed contacts: a real outage
 *   null  -- no evidence either way (before onboarding, or a device that emits no telemetry)
 * null is NOT "down". An AP onboarded two hours ago must not report a 22-hour outage.
 */
export type ConnectivityBucket = "up" | "gap" | null;

/** One row of GET /api/v2/devices/connectivity. */
export interface DeviceConnectivity {
  serial: string;
  /** Oldest-first, always exactly `hours` long and phase-aligned across every device. */
  buckets: ConnectivityBucket[];
  bucket_count: number;
  up: number;
  gap: number;
  /** up + gap — the denominator that makes "2 gaps" meaningful. */
  observed: number;
  unknown: number;
  /** True only with evidence AND no gaps. A device with no evidence is not stable. */
  stable: boolean;
}

/** The device's own uplink port (the one with the cable) and its negotiated link. */
export interface DeviceUplink {
  /** Local port id, e.g. "eth0" or "1/1/24". */
  port: string;
  /** Negotiated link speed in Mbps. */
  speed_mbps?: number;
  duplex?: "full" | "half";
}

/** A wall-plate AP physical ethernet port + its role/VLAN (the device "Ports" tab). */
export interface DevicePort {
  /** Physical port glob from the uCentral config, e.g. "WAN*" / "LAN*". */
  port: string;
  interface: string | null;
  /** uplink = upstream trunk; access = downstream, bridged to a (tagged) VLAN up the trunk. */
  role: "uplink" | "access";
  vlan: number | null;
  addressing: string | null;
}
export interface DevicePorts {
  serial: string;
  owgw_available: boolean;
  config_uuid: number | null;
  ports: DevicePort[];
}

/** One SSID a single AP is broadcasting (the device "SSIDs" panel). Extra config fields exist
 *  on the wire; these are the ones the panel renders. */
export interface DeviceSsidHit {
  name: string;
  bands: string[];
  security: string;
  hidden: boolean;
  vlan: number | null;
  /** Live associated-client count from statistics; null when owgw reported none. */
  clients: number | null;
  /** The AP's own report: true = broadcasting, false = configured (owgw holds it) but NOT on
   *  air, null/absent = the report carries no SSID list (OP#1814). */
  on_air?: boolean | null;
}

/** GET /api/v2/devices/{serial}/ssids — the AP's broadcasting SSIDs. */
export interface DeviceSsids {
  serial: string;
  owgw_available: boolean;
  ssids: DeviceSsidHit[];
}

/* ---- Global search (command palette) --------------------------------------------------- */

/** A device hit from GET /api/v2/search. org_id/site_id are numeric (JSON); the palette
 *  String()s them to build the scope-bearing route. */
export interface SearchDeviceHit {
  serial: string;
  name: string;
  model: string | null;
  kind: string;
  status: string | null;
  mac: string | null;
  /** Public IP (owgw connection source). */
  ip: string | null;
  /** Private / LAN management IP; the search matches on it too. */
  private_ip?: string | null;
  org_id: number;
  site_id: number | null;
  site_name: string | null;
}

/* ---- Gateway view (whitebox x86 uCentral gateway, read-only) ---------------------------- */

/** One interface of a uCentral gateway config as the "Gateway" panel renders it. */
export interface GatewayInterface {
  name: string | null;
  /** "upstream" (WAN) | "downstream" (LAN/VLAN). */
  role: string | null;
  ports: string[];
  vlan: number | null;
  ipv4: { addressing: string | null; subnet: string | null; gateway: string | null };
  dhcp: { lease_first: number | null; lease_count: number | null; lease_time: string | null } | null;
  ipv6: boolean;
  captive: boolean;
  services: string[];
  ssids: string[];
  /** Raw schema keys present on this interface (POC: what does this build populate?). */
  sections: string[];
}

export interface GatewaySummary {
  wan: string[];
  lan: string[];
  vlans: number[];
  dhcp_scopes: number;
  nat: boolean;
  captive: boolean;
  /** Top-level schema sections present in the config. */
  sections: string[];
  /** False when the config has no (or an empty) `metrics` section: the gateway reports state
   *  only at the agent default (600 s). Drives the "Enable state reporting" notice. */
  metrics_present?: boolean;
}

/** GET /api/v2/devices/{serial}/gateway. */
export interface DeviceGateway {
  serial: string;
  owgw_available: boolean;
  config_uuid: number | null;
  interfaces: GatewayInterface[];
  summary: GatewaySummary;
}

/* ---- Gateway networks: the EDITABLE LAN/VLAN table (Gateway > VLANs) -------------------
 *
 * The Meraki MX "Addressing & VLANs" row, but every field comes out of one uCentral
 * downstream interface. Note `subnet` and `interface_ip` are SEPARATE: the device schema
 * stores them as one string ("192.168.1.1/24" is the interface address WITH a prefix, not a
 * network), and the server splits it so the "Subnet" column never shows a host address.
 */
/**
 * What a gateway's WAN is doing right now, from its newest state document (upstream rows only).
 * `reported` false = the gateway has not reported an address yet; never a guess.
 */
export interface WanLive {
  reported: boolean;
  /** e.g. ["10.111.131.184/25"] */
  addresses: string[];
  primary: string | null;
  /** The config's addressing (dynamic | static | none), repeated for the display line. */
  addressing: string | null;
  gateway: string | null;
  dns: string[];
  /** Epoch seconds of the state record, or null. */
  recorded: number | null;
}

export interface GatewayNetworkRow {
  /** The uCentral interface name — the row identity for an edit or a delete. */
  name: string | null;
  /** 802.1Q id; null in Single LAN mode (the interface carries no `vlan` block). */
  vlan: number | null;
  /** NETWORK address, e.g. "192.168.100.0/24". */
  subnet: string | null;
  /** The gateway's OWN address on this network, e.g. "192.168.100.1". */
  interface_ip: string | null;
  addressing: string | null;
  ports: string[];
  /**
   * Is the network TAGGED on those ports (uCentral `ethernet[].vlan-tag`)?
   * true = tagged as it should be, false = the tag is missing or wrong (a VLAN network like
   * that egresses UNTAGGED and carries nothing), null = the interface declares no ports.
   */
  tagged: boolean | null;
  dhcp: { lease_first: number | null; lease_count: number | null; lease_time: string | null } | null;
  /** Meraki's 5th column. This firmware carries no policy object — always null. */
  group_policy: string | null;
  editable: boolean;
  /** True for the WAN/upstream row: editing it can strand the gateway permanently. */
  danger: boolean;
  danger_reason: string | null;
  /** Upstream (WAN) rows: the live address/gateway/DNS from the state document. */
  live?: WanLive | null;
}

export type GatewayLanMode = "single_lan" | "vlans";

/** The table itself — returned standalone and inside a plan's before/after. */
export interface GatewayNetworkTable {
  mode: GatewayLanMode;
  /** The EDITABLE networks (downstream interfaces). */
  networks: GatewayNetworkRow[];
  /** The LOCKED WAN row(s), rendered above the table with the danger reason. */
  upstream: GatewayNetworkRow[];
  lan_ports: string[];
  vlans_in_use: number[];
}

/** GET /api/v2/devices/{serial}/gateway/networks.
 *
 *  `editable` false + `reason` is the honest gate: a gateway whose controller-side config is
 *  empty (running its factory config) cannot accept an edit, and the screen says so instead
 *  of offering an Add button whose push the device would reject. */
export interface GatewayNetworks extends GatewayNetworkTable {
  serial: string;
  config_uuid: number | null;
  owgw_available: boolean;
  editable: boolean;
  reason: string | null;
  /** Present when editable. False = no `metrics` section (see GatewaySummary.metrics_present). */
  metrics_present?: boolean;
}

/** The write path's intents. DHCP adds to this union, not to the endpoints. */
export type GatewayIntent =
  | "networks.upsert"
  | "networks.delete"
  | "networks.mode"
  | "dhcp.scope"
  | "dhcp.reservation.upsert"
  | "dhcp.reservation.delete"
  /* Gateway > Firewall. The four controls uCentral can actually enforce. */
  | "firewall.forward.upsert"
  | "firewall.forward.delete"
  | "firewall.isolation"
  | "firewall.blocked"
  /* Enable state reporting: add the standard `metrics` block when absent (OP #1678). */
  | "metrics.ensure";

/** The spec half of a plan/apply body. Which fields matter depends on the intent. */
export interface GatewayChangeSpec {
  /* networks.upsert */
  name?: string;
  /** Present => this is an EDIT of that row. Absent => an ADD, which refuses a name clash. */
  original_name?: string | null;
  vlan?: number | null;
  interface_ip?: string;
  prefix?: number;
  dhcp_enabled?: boolean;
  dhcp_lease_first?: number;
  dhcp_lease_count?: number;
  dhcp_lease_time?: string;
  /* networks.delete */
  names?: string[];
  /* networks.mode */
  mode?: GatewayLanMode;
  /* dhcp.* -- the LAN network (downstream interface name) the change addresses */
  interface?: string;
  /* dhcp.scope (+ dhcp_enabled / dhcp_lease_* above, shared with networks.upsert) */
  use_dns?: string[];
  /* dhcp.reservation.upsert */
  macaddr?: string;
  /** Present => an EDIT of that assignment (the MAC may change). */
  original_macaddr?: string | null;
  /** The operator's address; the server converts it to the schema's offset. */
  address?: string;
  lease_time?: string;
  publish_hostname?: boolean;
  /* dhcp.reservation.delete */
  macaddrs?: string[];
  /* firewall.forward.upsert */
  protocol?: "tcp" | "udp" | "any";
  /** A port ("443") or an inclusive range ("8000:8010"). */
  external_port?: string | number;
  /** Defaults to external_port server-side, but is always written explicitly. */
  internal_port?: string | number;
  /** The real address of the target device; the server converts it to the masked offset. */
  internal_address?: string;
  /** Both present => an EDIT of that forward (the external port may change). */
  original_external_port?: string | number | null;
  original_protocol?: "tcp" | "udp" | "any" | null;
  /* firewall.forward.delete */
  forwards?: Array<{ protocol: string; external_port: string | number }>;
  /* firewall.isolation */
  isolate?: boolean;
  /* firewall.blocked -- the FULL intended list; an empty array clears it. */
  subnets?: string[];
}

/** POST .../gateway/plan — the PROPOSE half. Read-only; nothing is written anywhere. */
export interface GatewayPlan {
  serial: string;
  intent: string;
  changed: boolean;
  config_uuid: number | null;
  /** Required by /apply, and re-derived there from a fresh fetch: a config that moved since
   *  the preview invalidates it rather than pushing a diff nobody reviewed. */
  plan_token: string;
  diff: ConfigActionDiffEntry[];
  /** Operator-facing cautions. Never blocks; always rendered. */
  warnings: string[];
  networks_before: GatewayNetworkTable;
  networks_after: GatewayNetworkTable;
  /** The DHCP screen's before/after from the same fetch (configuration only, no state). */
  dhcp_before?: GatewayDhcpTable;
  dhcp_after?: GatewayDhcpTable;
}

/* ---- Gateway DHCP (Gateway > DHCP) --------------------------------------------------- */

/** Whether the firmware APPLIES fixed IP assignments. Never `true` from a version string:
 *  false = known broken (<= TIP v5.1.0-rc2) or version unknown; null = newer, unverified. */
export interface GatewayReservationSupport {
  functional: false | null;
  firmware: string | null;
  reason: string;
}

/** One `ipv4.dhcp-leases[]` entry. Address is DERIVED from subnet + offset (configured). */
export interface GatewayDhcpReservation {
  macaddr: string;
  static_lease_offset: number | null;
  resolved_address: string | null;
  lease_time: string;
  publish_hostname: boolean;
  in_pool: boolean;
  /** Controller-side only. Never part of the pushed configuration. */
  label: string | null;
  functional: false | null;
}

export type GatewayLeaseState = "observed" | "not_observed" | "interface_down" | "no_state";

/** Device-REPORTED lease evidence for one scope, with its provenance. */
export interface GatewayLeaseVisibility {
  state: GatewayLeaseState;
  supported: boolean;
  /** Epoch seconds of the state record this came from. */
  recorded: number | null;
  detail: string;
  items: { mac: string | null; address: string | null; hostname: string | null; assigned: number | null }[];
}

export interface GatewayDhcpScope {
  /** "/interfaces/<i>" -- the config-to-state join key. */
  location: string;
  name: string | null;
  role: string | null;
  vlan: number | null;
  ports: string[];
  addressing: string | null;
  /** NETWORK, e.g. 192.168.1.0/24. */
  subnet: string | null;
  interface_ip: string | null;
  host_count: number | null;
  dhcp_capable: boolean;
  not_capable_reason: string | null;
  enabled: boolean;
  dhcp: {
    lease_first: number | null;
    lease_count: number | null;
    lease_time: string | null;
    use_dns: string[];
  } | null;
  pool: { first_address: string; last_address: string; size: number } | null;
  reservations: GatewayDhcpReservation[];
  /** REPORTED by the device; null when there is no state for this interface. */
  reported_addresses: string[] | null;
  leases: GatewayLeaseVisibility;
  /** Upstream rows only. */
  danger_reason?: string | null;
}

export interface GatewayDhcpTable {
  scopes: GatewayDhcpScope[];
  upstream: GatewayDhcpScope[];
}

/** GET /api/v2/devices/{serial}/gateway/dhcp. */
export interface GatewayDhcp extends GatewayDhcpTable {
  serial: string;
  config_uuid: number | null;
  owgw_available: boolean;
  editable: boolean;
  reason: string | null;
  reservations_support: GatewayReservationSupport;
  /** Provenance of every REPORTED value on the page. */
  state: {
    available: boolean;
    uuid: number | null;
    recorded: number | null;
    matches_config: boolean | null;
  };
}

/** POST .../gateway/apply — the CONFIRM half. This one writes to the live device. */
export interface GatewayApplyResult {
  serial: string;
  intent: string;
  changed: boolean;
  config_uuid: number | null;
  plan_token: string;
  /** Read-back verdict. A pure VLAN-id edit can only ever be UNKNOWN — a uCentral state
   *  document reports no VLAN membership, so nothing can confirm it. */
  verification: { verdict?: string; reason?: string | null } | null;
}

/* ---- Gateway "Re-apply managed settings" (POST /gateway/reassert) ------------------- */

/** Which controller-owned config-raw fold a line belongs to; null = NOT managed. */
export type GatewayManagedFold = "vlan_wan_forwarding" | "shasta_sinkhole" | "ha_dhcp_force";

/** One config-raw line (a uci batch line, e.g. ["set", "dhcp.down1v0.force", "1"]). */
export interface GatewayRawLine {
  line: unknown[];
  fold: GatewayManagedFold | null;
}

/** Line-level diff of a re-assert (services/gateway_config.reassert_summary). */
export interface GatewayReassertSummary {
  before_lines: number;
  after_lines: number;
  added: GatewayRawLine[];
  removed: GatewayRawLine[];
  /** Removals outside the managed blocks: the apply is refused unless confirmed. */
  unexpected: { line?: unknown[]; path?: string; reason: string }[];
  reordered: boolean;
  folds: Record<GatewayManagedFold, { added: number; removed: number }>;
  changed: boolean;
  ha_member: boolean;
  requires_confirmation: boolean;
}

/** Dry-run response: the normal gateway plan plus the managed-line diff. */
export interface GatewayReassertPlan extends GatewayPlan {
  reassert: GatewayReassertSummary;
}

/** POST /sites/{id}/gateways/reassert dry run: every gateway, in APPLY order. */
export interface SiteReassertPlan {
  site_id: number;
  site_name: string;
  plan_token: string;
  can_apply: boolean;
  requires_confirmation: boolean;
  gateways: {
    serial: string;
    name: string;
    role: "primary" | "secondary" | "single";
    changed: boolean;
    plan_token: string | null;
    reassert: GatewayReassertSummary | null;
    warnings: string[];
    error: string | null;
  }[];
  refusals: { serial: string | null; message: string }[];
}

/** GET /sites/{id}/gateways/reassert/status -- read back from the audit log. */
export interface SiteReassertRun {
  run_id: string;
  status: "running" | "succeeded" | "failed" | "interrupted";
  error: string | null;
  started_at: string;
  finished_at: string | null;
  started_by: string | null;
  gateways: {
    serial: string;
    name: string | null;
    role: string | null;
    status: "pending" | "applied" | "no_op" | "failed" | "skipped";
    error: string | null;
    reported_config_uuid: number | null;
  }[];
}

/* ---- Client location ("where is this MAC?") ------------------------------------------- */

/** Compact device pointer inside a client-location answer (enough to render + deep-link). */
export interface ClientLocationDevice {
  serial: string;
  name: string;
  model: string | null;
  kind: string;
  status: string | null;
  org_id: number;
  site_id: number | null;
  site_name: string | null;
}

/** The client's live association right now (null when offline). */
export interface ClientLocationCurrent {
  ssid: string | null;
  band: string | null;
  channel: number | null;
  channel_width: number | null;
  rssi: number | null;
  experience: string | null;
  ip: string | null;
  connected_sec: number | null;
  rx_rate_mbps: number | null;
  tx_rate_mbps: number | null;
  retry_pct: number | null;
}

/** The wireless path: AP -> uplink -> LLDP neighbour -> switch (+ FDB-confirmed port). */
export interface ClientLocationPath {
  ap: ClientLocationDevice;
  uplink_port: string | null;
  lldp: { system_name: string | null; port_id: string | null; mgmt_ip: string | null; vlan: number | null };
  switch: ClientLocationDevice | null;
  switch_port: string | null;
  /** "fdb" = confirmed from the switch's MAC table; "lldp" = what the switch advertised. */
  switch_port_source: "fdb" | "lldp" | null;
}

/** A MAC found on a switch port with no wireless sighting (wired client). */
export interface ClientLocationWired {
  switch: ClientLocationDevice;
  port: string | null;
  vlan: number | null;
}

/** One AP that has seen the MAC (the roaming trail, newest first). */
export interface ClientTrailHop {
  device_serial: string;
  device_name: string;
  site_id: number | null;
  site_name: string | null;
  ssid: string | null;
  band: string | null;
  ip: string | null;
  rssi: number | null;
  first_seen: string | null;
  last_seen: string | null;
}

/**
 * Bandwidth usage for the live association (null when offline). Derived server-side from the
 * same client row as `current` (services/client_usage.summarise_client): cumulative bytes since
 * association plus the sustained average over the connected window. `ratable` is false for a
 * session too brief to average — show the totals, not the rate.
 */
export interface ClientLocationUsage {
  upload_bytes: number;
  download_bytes: number;
  total_bytes: number;
  avg_bps: number | null;
  ratable: boolean;
}

/** GET /api/v2/clients/{mac}/location. */
export interface ClientLocation {
  mac: string;
  vendor: string | null;
  hostname: string | null;
  found: boolean;
  online: boolean;
  current: ClientLocationCurrent | null;
  usage: ClientLocationUsage | null;
  path: ClientLocationPath | null;
  wired: ClientLocationWired | null;
  trail: ClientTrailHop[];
}

/** A client (by MAC) resolved to the AP + site it was last seen on. */
export interface SearchClientHit {
  mac: string;
  hostname?: string | null;
  ip?: string | null;
  ssid?: string | null;
  status: string | null;
  org_id: number;
  device_serial: string | null;
  device_name: string | null;
  site_id: number | null;
  site_name: string | null;
  last_seen: string | null;
}

export interface SearchSiteHit {
  id: number;
  name: string;
  org_id: number;
}

/** A catalog (config) object hit: SSID/RADIUS/ACL/firewall/radio/port profile, or a Template.
 *  `kind` is the backend CatalogKindEnum value ("ssid", "radius", …) or "template"; the palette
 *  maps it to a section route. Deliberately MINIMAL — the backend never sends secret refs here.
 *  `detail` is a short human line (e.g. a RADIUS host:port, an SSID's security mode). */
export interface SearchConfigHit {
  kind: string;
  id: number;
  name: string;
  org_id: number;
  detail: string;
}

/** GET /api/v2/search — categorized global-search results. */
export interface SearchResults {
  query: string;
  devices: SearchDeviceHit[];
  clients: SearchClientHit[];
  sites: SearchSiteHit[];
  configs: SearchConfigHit[];
}

/** One source behind an assistant answer (a KB document/section). `url` links to the source when
 *  kb-service has one (a GitLab blob URL). */
export interface AssistantCitation {
  n: number;
  title: string;
  heading: string;
  document_id: number | null;
  url: string | null;
}

/** POST /api/v2/assistant/ask — a KB-grounded answer (AI governance P0-A semantics):
 *  `grounded` is true ONLY when a model answered AND cited a retrieved snippet; `model` is the model
 *  that wrote the answer (null when none did); `available` is false when the model was asked and no
 *  backend answered — the answer is then a fixed "unavailable" sentence with no citations. */
export interface AssistantAnswer {
  answer: string;
  citations: AssistantCitation[];
  grounded: boolean;
  model: string | null;
  available: boolean;
}

/** The failure domain a root-cause diagnosis places a device in (services/ai/rootcause). Coarse on
 *  purpose: it tells the operator WHICH subsystem to look at first, not a false-precision verdict. */
export type DiagnosisDomain =
  | "telemetry_unavailable"
  | "certificate"
  | "connectivity"
  | "uplink"
  | "healthy"
  | "unknown";

/** Our CONFIDENCE in the failure_domain verdict (not a probability of failure). */
export type DiagnosisLikelihood = "high" | "medium" | "low";

/** POST /api/v2/assistant/diagnose — the READ-ONLY, advisory root-cause result for one device,
 *  grounded first in the device's LIVE owgw facts and second in the KB. findings / next_actions /
 *  failure_domain are ALWAYS deterministic. `narrative` is the model's text — server-side it is only
 *  ever non-null for msp_admin / platform_admin (AI governance P0-A) and only when a model answered;
 *  `model` is the model that wrote it (else null). `grounded` = narrative AND KB citations.
 *  Mirrors services/ai/rootcause.diagnose() exactly. */
export interface DeviceDiagnosis {
  failure_domain: DiagnosisDomain;
  likelihood: DiagnosisLikelihood;
  findings: string[];
  next_actions: string[];
  citations: AssistantCitation[];
  grounded: boolean;
  model: string | null;
  narrative: string | null;
}

/* ------------------------------------------------------------------ *
 * Assistant confirm-gated ACTIONS (Phase 2) — propose -> confirm/reject
 *
 * The write half of the assistant (api/v2/assistant_proposals.py). An operator turns a gateway
 * change into a reviewable AiProposal that carries a REAL diff; a human then CONFIRMS it (which
 * pushes through the same manual gateway write path a hand-edit uses) or REJECTS it. The model
 * never authors the diff and never names the target: deterministic Python builds `proposed`, so the
 * UI only ever sends the same {intent, spec} the manual form builds, plus optional rationale prose.
 * ------------------------------------------------------------------ */

/** AiProposalStatusEnum.value — the proposal lifecycle. Terminal states are immutable. */
export type AiProposalStatus = "pending" | "confirmed" | "discarded";

/**
 * The `proposed` payload for a gateway.config proposal (services/ai/actions._plan_gateway_config).
 *
 * ⚠ EVERY `_`-prefixed key is SERVER-INTERNAL confirm-time machinery — the frozen target/intent/spec,
 * the plan token, the freshness fingerprint and the pre-change snapshot. The UI MUST NOT render them;
 * only `_expired` is read, to tell a drift-expired discard from a plain human reject. The shown
 * review is built from `diff` (via DiffView), `warnings`, and networks_before/after. The other
 * underscore keys are deliberately NOT typed here so no component can reach for them by accident.
 */
export interface AiProposalGatewayConfig {
  kind: "gateway.config";
  /** The target gateway's serial. */
  serial: string;
  /** The gateway intent that was planned, e.g. "dhcp.scope". */
  intent: string;
  changed: boolean;
  /** The canonical config diff — the ONLY diff the card renders (via DiffView). */
  diff: ConfigActionDiffEntry[];
  /** Operator-facing cautions from the plan. Never blocks; always rendered. */
  warnings: string[];
  networks_before: GatewayNetworkTable;
  networks_after: GatewayNetworkTable;
  /** Set true (with `_expired_reason`) ONLY when a confirm found the live config had drifted and
   *  marked the proposal terminal. Lets the card say "expired — re-propose" vs a plain reject. */
  _expired?: boolean;
  _expired_reason?: string;
}

/** GET/POST .../assistant/proposals* — one AiProposal (mirrors AiProposal.to_dict exactly). */
export interface AiProposal {
  id: number;
  org_id: number;
  /** NULL for an org-scoped kind; set for a site-scoped kind (gateway.config is site-scoped). */
  site_id: number | null;
  kind: string;
  /** The structured review payload. Typed to the only kind this phase ships (gateway.config). */
  proposed: AiProposalGatewayConfig;
  rationale: string | null;
  /** Who wrote `rationale`: "model" (AI-generated; only ever served to msp_admin+), "deterministic"
   *  (the detector's own numbers — what customer roles get), or "operator" (a human's typed note). */
  rationale_source?: "model" | "deterministic" | "operator";
  confidence: number | null;
  model: string | null;
  status: AiProposalStatus;
  /** Who authored it: the operator for a human-built proposal (gateway.config), "ai" for an
   *  AI-filed one (anomaly explanations). */
  created_by: string | null;
  /** The human who confirmed or discarded it. */
  decided_by: string | null;
  correlation_id: string | null;
  created_at: string;
  decided_at: string | null;
}

/** POST /api/v2/assistant/propose — body. `spec` is the SAME {intent, spec} the manual plan/apply
 *  path takes; rationale/model/confidence are the only model-authored (here: operator-noted) inputs. */
export interface ProposeActionBody {
  kind: "gateway.config";
  /** The target gateway serial. */
  target: string;
  spec: { intent: GatewayIntent; spec: GatewayChangeSpec };
  rationale?: string;
  model?: string;
  confidence?: number;
}

/** POST /api/v2/assistant/proposals/<id>/confirm — 200 body (the accountable apply). */
export interface ConfirmProposalResult {
  proposal: AiProposal;
  /** The result of the real device write, through the manual gateway apply path. */
  result: GatewayApplyResult;
}

/** GET /api/v2/assistant/proposals — the list envelope (api/v2/_helpers.list_envelope). */
export interface AiProposalList {
  items: AiProposal[];
  count: number;
  total: number;
  limit: number;
  offset: number;
}

/** A registered firmware image for a device model (the panel's "latest available" baseline).
 *  Named Device* to avoid colliding with the Network>Firmware rollout list's FirmwareImage
 *  (declared locally in lib/api/hooks.ts) -- these are two distinct serializations. */
export interface DeviceFirmwareImage {
  id: number;
  version: string;
  device_type: string;
  file_size: number;
  release_notes: string | null;
  is_stable: boolean;
  created_at: string;
}

/** The firmware update state machine: intent -> flashing -> settled. */
export type FirmwareUpdateStatus =
  | "pending"
  | "downloading"
  | "applying"
  | "completed"
  | "failed";

/** One firmware update attempt as the device panel sees it (FirmwareUpdate.to_dict). Distinct
 *  from the rollout-list FirmwareUpdate in hooks.ts (which carries device_serial/version). */
export interface DeviceFirmwareUpdate {
  id: number;
  firmware_version: string | null;
  status: FirmwareUpdateStatus;
  progress: number;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

/** GET /devices/{serial}/firmware — the device "Firmware" panel payload. */
export interface DeviceFirmware {
  serial: string;
  model: string | null;
  current_version: string | null;
  latest_available: DeviceFirmwareImage | null;
  latest_stable: DeviceFirmwareImage | null;
  latest_update: DeviceFirmwareUpdate | null;
  /** Executor on AND a reachable download host configured -> a triggered upgrade fires now. */
  executor_enabled: boolean;
  download_host_configured: boolean;
}

/** A named wired network (label + VLAN) at a site -- the building block for port profiles. */
export interface WiredNetwork {
  id: number;
  site_id: number;
  name: string;
  vlan_id: number;
}
/** One port's assignment inside a profile.
 *  - Access: bound to a site VLAN directly (`vlan`), or to a named wired network
 *    (`network_id`, legacy/back-compat), or neither (untagged/native). At most one of the two.
 *  - Uplink: the trunk. `allowed_vlans` is an optional trunk allow-list (sorted, deduped VLAN
 *    ids); omitted/empty means the trunk carries every VLAN (the original behaviour). */
export interface PortMapEntry {
  role: "uplink" | "access";
  network_id?: number | null;
  vlan?: number | null;
  allowed_vlans?: number[];
  /** Friendly wall-plate name for an access port ("TV", "AppleTV"). Meridian's source of truth
   *  for the port name; also used as the rendered interface name where the uCentral schema
   *  allows (uCentral has no per-port description primitive, so it is not guaranteed on-device). */
  label?: string | null;
}
/** A reusable wall-plate AP port profile: a name + per-port map (port-glob -> assignment). */
export interface ApPortProfile {
  id: number;
  site_id: number;
  name: string;
  ports: Record<string, PortMapEntry>;
  /** The site's default profile (Meraki CURRENT DEFAULT). Pre-selection + label only: it
   *  pushes nothing on its own, so flipping it can never rewrite a live AP. */
  is_default: boolean;
  /** OP#1937: APs at the site carrying this profile (list + PUT responses). Saving a profile
   *  never pushes, so these APs keep the OLD port config until the profile is applied. */
  used_by?: number;
  used_by_serials?: string[];
}

/* ---------------------------------------------------------------------------------------- *
 * Per-AP PORT PLANE — the profile is the base layer, an override is a per-(device, port)
 * exception. `source` is the whole point of the feature and the UI renders all three states
 * differently: a port with no override keeps INHERITING, so a later profile edit reaches it.
 * ---------------------------------------------------------------------------------------- */
export type ApPortSource = "override" | "profile" | "device";

export interface ApPortValue {
  role: "uplink" | "access";
  vlan: number | null;
  network_id: number | null;
  network_name: string | null;
  /** The port's friendly wall-plate name from the profile ("TV"); null when unnamed. */
  label?: string | null;
  /** The mapping points at a wired network that has since been deleted. Readable, NOT
   *  pushable — the backend refuses to plan it rather than sending the port out untagged. */
  network_missing: boolean;
}

export interface ApPortRow {
  /** Logical uCentral port name ("LAN2"), or a glob when the inventory fell back to the
   *  config (no capabilities => one glob is one port and no per-port split is possible). */
  port: string;
  uplink: boolean;
  /** False = this screen will not change it. `reason` says why (uplink; not in the config). */
  manageable: boolean;
  reason: string | null;
  interface: string | null;
  source: ApPortSource;
  /** True when an override ROW exists for this port — presence, never a value comparison. */
  overridden: boolean;
  effective: ApPortValue;
  profile: (ApPortValue & { glob: string }) | null;
  /** Only meaningful when overridden: the override happens to equal the profile's value. It
   *  is still an override — this only lets the UI say so. */
  matches_profile: boolean | null;
  live: { role: "uplink" | "access"; vlan: number | null };
}

export interface ApPortsView {
  serial: string;
  owgw_available: boolean;
  config_uuid: number | null;
  editable: boolean;
  reason: string | null;
  /** "capabilities" = the AP's own port count; "config" = the globs its config uses. */
  port_source: "capabilities" | "config" | null;
  profile: { id: number; name: string } | null;
  ports: ApPortRow[];
}

/** One requested change. `revert` DELETES the override row so the port inherits again. */
export interface ApPortChange {
  port: string;
  action: "override" | "revert";
  role?: "access";
  network_id?: number | null;
  vlan?: number | null;
}

/** POST .../ap-ports/plan — the PROPOSE half. Writes nothing, anywhere. */
export interface ApPortsPlan {
  serial: string;
  changed: boolean;
  config_uuid: number | null;
  /** Required by /apply and re-derived there from a fresh fetch. */
  plan_token: string;
  diff: ConfigActionDiffEntry[];
  warnings: string[];
  ports_before: ApPortRow[];
  ports_after: ApPortRow[];
}

/** POST .../ap-ports/apply — the CONFIRM half. This one writes to the live AP. */
export interface ApPortsApplyResult {
  serial: string;
  changed: boolean;
  config_uuid: number | null;
  plan_token: string;
  ports: ApPortRow[];
  /** A port VLAN change can only ever come back UNKNOWN: a uCentral state document reports
   *  no port VLAN membership at all, so nothing can confirm it. */
  verification: { verdict?: string; reason?: string | null } | null;
}

/** LLDP neighbor: the switch (and remote port) this device is physically plugged into. */
export interface LldpNeighbor {
  /** Remote switch system name, e.g. "JediHome-6100-IDF". */
  system_name?: string;
  /** Remote port id, e.g. "1/1/7". */
  port_id?: string;
  /** Remote management IP. */
  mgmt_ip?: string;
  /** Access/native VLAN on that switch port. */
  vlan?: number;
  /**
   * When the neighbour was last confirmed (OP#1857). DERIVED by the controller from the
   * device's own reporting clock (models/device.lldp_as_of): the snapshot is never cleared,
   * so an offline AP still names its old switch -- this says how old that is.
   */
  as_of?: string | null;
  /** True when it must not read as current (device offline / not re-confirmed recently). */
  stale?: boolean;
}

/** One physical port on a switch: link state, VLAN, PoE, and its LLDP neighbor. */
export interface SwitchPort {
  /** Port id, e.g. "1/1/7". */
  name: string;
  status: "up" | "down" | "disabled";
  speed_mbps?: number | null;
  duplex?: "full" | "half" | null;
  /** Access/native VLAN. */
  vlan?: number | null;
  /** PoE state + delivered watts (null on non-PoE ports). */
  poe?: { enabled: boolean; powered_w?: number | null } | null;
  /** What is plugged into this port, from LLDP. */
  lldp?: LldpNeighbor | null;
}

/**
 * One IPv4 a device reported on its own interface (services/device_private_ip).
 * role: upstream | downstream | svi | unknown, or dhcp_lease when it was learned from the
 * gateway's lease table (then `iface` is the gateway's name).
 */
export interface DeviceAddress {
  ip: string;
  prefix: number | null;
  iface: string | null;
  role: "upstream" | "downstream" | "svi" | "unknown" | "dhcp_lease";
  vlan: number | null;
}

export interface Device {
  serial: string;
  org_id: string;
  site_id: string | null;
  /** Assigned site's name; null => Unassigned (device not yet placed at a site). */
  site_name?: string | null;
  /** AP groups (a group is a tag). */
  tags?: string[];
  /** Applied wall-plate AP port profile (null => none). */
  port_profile_id?: number | null;
  port_profile_name?: string | null;
  name: string;
  /** Installer note: where it is / how it is mounted ("Basement, behind the TV"). null => none. */
  description?: string | null;
  /** Installer photos on file (0-3). null when the endpoint did not batch the count. */
  photo_count?: number | null;
  model?: string;
  /** Derived category: access_point / switch / firewall / gateway / other. */
  kind?: string;
  /** Device MAC (base/management). */
  mac?: string;
  firmware?: string;
  /**
   * ISO-8601; when the DEVICE was last in contact (owgw `lastContact`).
   * NULL means it has NEVER contacted us — render "never", not a date. This is the field
   * `dormant` is derived from.
   */
  last_seen: string | null;
  /**
   * ISO-8601; when the controller first registered the device ("first seen"). Additive
   * (2026-10-19, Quarantine panel); absent on older controllers, so optional.
   */
  registered_at?: string | null;
  /**
   * ISO-8601; the last state DOCUMENT the device produced (owgw `lastRecordedContact`).
   * A device can hold an open control channel and have stopped producing state — measured on
   * the live fleet, one AP's control channel was 111s old while its last state document was
   * 2h44m old. Against last_seen alone that device reads as perfectly healthy.
   */
  last_state_report_at?: string | null;
  online: boolean;
  health: HealthState;
  /** Precise lifecycle status for the pill: online|degraded|offline|provisioning. */
  status?: DeviceStatus;
  /** PUBLIC IP: where owgw saw the device connect from (the site's NAT egress behind NAT). */
  ip?: string | null;
  /**
   * PRIVATE IP: the device's own LAN / management address (gateway: its LAN; AP/switch: its
   * upstream DHCP / management-SVI address). null until a state document or a gateway DHCP
   * lease has reported one.
   */
  private_ip?: string | null;
  /** Every own address from the last usable report (detail page). [] when none. */
  private_addresses?: DeviceAddress[];
  clients_count?: number;
  /** Uplink port + negotiated speed (the port with the cable / uplink). */
  uplink?: DeviceUplink;
  /** Where it is physically plugged in, learned from LLDP. */
  lldp?: LldpNeighbor;
  /** For switches: per-port link/VLAN/PoE/LLDP. Absent on APs. */
  ports?: SwitchPort[];
  /*
   * clients_count (above) is CONNECTED NOW: a fresh sighting on an ONLINE device
   * (services/client_counts), 0 while offline. These two are the offline device's
   * last-reported count and when -- null while online or when it never reported.
   */
  clients_last_known?: number | null;
  clients_last_known_at?: string | null;
}

/** One installer photo (GET /api/v2/devices/{serial}/photos). URLs are authenticated API
 *  routes on the same origin: the session cookie authorizes them; there is no public link. */
export interface DevicePhoto {
  id: number;
  /** 1..3: the photo's position in the device's strip. */
  slot: number;
  /** The STORED type (server-sniffed, after re-encode). */
  content_type: string;
  size_bytes: number;
  sha256: string;
  width: number | null;
  height: number | null;
  caption: string | null;
  /** false only for a HEIC whose embedded metadata could not be removed. */
  metadata_stripped: boolean;
  /** false for HEIC (stored as uploaded, no server thumbnail). */
  has_thumbnail: boolean;
  uploaded_by: string | null;
  created_at: string | null;
  image_url: string;
  thumbnail_url: string;
}

export interface DevicePhotoList {
  serial: string;
  max_photos: number;
  count: number;
  items: DevicePhoto[];
}

export interface Client {
  mac: string;
  /** Serial of the AP/switch the client is on. */
  device_serial: string;
  /** Display name of that AP/device (resolved server-side for convenience). */
  device_name?: string | null;
  hostname?: string | null;
  /** OUI-derived vendor ("Apple"), or "Randomized" for a private MAC. */
  vendor?: string | null;
  ip?: string | null;
  ssid?: string | null;
  /** Radio band the client associated on: "2G" | "5G" | "6G". */
  band?: string | null;
  channel?: number | null;
  channel_width?: number | null;
  rssi?: number | null;
  /** Link-quality bucket derived from RSSI (capped by low PHY rate / high retries). */
  experience?: "excellent" | "good" | "fair" | "poor" | "unknown" | string | null;
  /** Cumulative airtime retry rate (% of tx packets retried), or null when unknown. */
  retry_pct?: number | null;
  /** Current PHY rates (Mbps). */
  rx_rate_mbps?: number | null;
  tx_rate_mbps?: number | null;
  /** Session byte counters. */
  rx_bytes?: number | null;
  tx_bytes?: number | null;
  /** Seconds the client has been associated. */
  connected_sec?: number | null;
  status?: "online" | "offline" | string | null;
  // --- legacy/optional (not emitted by the live source yet) ---
  org_id?: string;
  site_id?: string | null;
  vlan?: number | null;
  policy?: "normal" | "blocked" | "allowed" | string | null;
  manufacturer?: string | null;
  connected_since?: string | null;
}

/* ------------------------------------------------------------------ *
 * Health worklist  (drives the health-first dashboard)
 * ------------------------------------------------------------------ */

export interface HealthAlert {
  id: string;
  org_id: string;
  site_id: string | null;
  severity: Exclude<HealthState, "unknown">;
  /** Machine kind so the UI can route to the right detail / Explain surface. */
  kind: string; // e.g. "device_offline" | "radio_saturation" | "anomaly"
  title: string;
  detail?: string;
  target_type: "device" | "client" | "site" | "template";
  target_id: string;
  created_at: string;
  /** Present when an AI proposal exists to explain/remediate (Phase 3). */
  proposal_id?: string | null;
  /* --- Drill-down fields (OP#1854): which device, where, since when. Optional so older
   *     controllers and the mocks build still type-check. --- */
  /** Last time the condition was confirmed (the monitor refreshes it every pass). */
  updated_at?: string | null;
  /** The device's hostname, when it has one. */
  device_name?: string | null;
  device_model?: string | null;
  /** Derived device kind: access_point | switch | gateway | ... */
  device_kind?: string | null;
  /** Last contact from the device; for an offline alert this is "offline since". */
  device_last_seen?: string | null;
  /** Last known uplink (LLDP neighbour + port) with its age (OP#1854 AC); null when none. */
  device_lldp?: LldpNeighbor | null;
  site_name?: string | null;
}

/** GET /api/v2/health/worklist -- the list envelope plus per-severity counts for the scope. */
export interface HealthWorklist {
  items: HealthAlert[];
  total: number;
  next_cursor?: string | null;
  /** Counts for the scope after the kind filter, before the severity filter. */
  summary?: { total: number; critical: number; warn: number };
}

/* ------------------------------------------------------------------ *
 * Canonical catalog + review  (Phase 2 surface, typed now)
 * ------------------------------------------------------------------ */

export type CatalogKind =
  | "ssid"
  | "network"
  | "radius"
  | "acl"
  | "firewall_policy"
  | "radio_profile"
  | "port_profile"
  | "template";

export interface CatalogObject {
  id: string;
  org_id: string;
  kind: CatalogKind;
  name: string;
  version: number;
  /** Ruckus EDITABLE=false analog — system defaults are non-destructible. */
  is_system: boolean;
  created_by: string;
  updated_at: string;
}

/**
 * The ONE canonical diff shape. Emitted identically by resolve-preview, AI
 * proposal, and audit before/after — rendered by the single DiffView so the
 * "review the diff, confirm" ritual transfers everywhere (seam #4).
 */
export interface ConfigDiff {
  /** Human summary line, e.g. "3 changed, 1 added". */
  summary: string;
  /** Optional blast-radius line for apply-to-many / AI proposals. */
  blast_radius?: string;
  changes: DiffChange[];
}

export interface DiffChange {
  op: "add" | "remove" | "change";
  /** Dotted path into the resolved flat payload, e.g. "ssids[0].passphrase". */
  path: string;
  before?: unknown;
  after?: unknown;
  /** True when the value is a secret ref — rendered masked, never revealed. */
  secret?: boolean;
}

/* ------------------------------------------------------------------ *
 * Device configuration ACTIONS — the "Configuration" panel (owgw push)
 * ------------------------------------------------------------------ */

/** One curated, safe config change an operator can apply from the Device page.
 *  scope: "device" = this AP/interface only; "site" = applied to every device in
 *  the site (syslog/SNMP/NTP...). owgw is the southbound; each apply is audited. */
export interface DeviceConfigAction {
  id: string;
  label: string;
  description: string;
  scope: "device" | "site";
  /** Already active on the device's live config. */
  applied: boolean;
  /** Applicable to this device (e.g. it actually has an uplink interface). */
  supported: boolean;
}

/** GET /devices/{serial}/config/actions */
export interface DeviceConfigActions {
  serial: string;
  config_uuid: number | null;
  /** False when owgw has no config for this device (unreachable/unmanaged). */
  owgw_available: boolean;
  actions: DeviceConfigAction[];
}

/** One entry of the top-level config diff (backend diff_payload shape). */
export interface ConfigActionDiffEntry {
  path: string;
  op: "added" | "removed" | "changed";
  before?: unknown;
  after?: unknown;
}

/** POST .../{action_id}/preview — the redacted before/after, no write. */
export interface ConfigActionPreview {
  action_id: string;
  changed: boolean;
  supported: boolean;
  diff: ConfigActionDiffEntry[];
}

/** POST .../{action_id}/apply — the result of the accountable write. */
export interface ConfigActionApplyResult {
  action_id: string;
  changed: boolean;
  applied: boolean;
  config_uuid: number | null;
}

/* ------------------------------------------------------------------ *
 * SITE configuration ACTIONS — the Site "Configure" page (fan-out)
 * ------------------------------------------------------------------ */

/** One site-wide config action. Applying it fans out across every device in the
 *  site. state aggregates the per-device applied status. */
export interface SiteConfigAction {
  id: string;
  label: string;
  description: string;
  scope: "site";
  /** Optional operator-input schema for parameterized services (syslog/SNMP). */
  fields: { name: string; label: string; type: string; required?: boolean }[];
  /** Devices in the site the action applies to, and how many already have it. */
  supported_count: number;
  applied_count: number;
  /** "applied" (all supported have it), "partial" (some), or "none". */
  state: "applied" | "partial" | "none";
  /** Devices this action never touches (switches, HA gateways, ...) and why. */
  excluded_devices?: SiteActionExcludedDevice[];
}

/** A device a site action skips by policy, with the operator-facing reason. */
export interface SiteActionExcludedDevice {
  serial: string;
  name: string;
  kind: string | null;
  reason: string;
}

/** GET /sites/{id}/config/actions */
export interface SiteConfigActions {
  site_id: number;
  device_count: number;
  owgw_available: boolean;
  actions: SiteConfigAction[];
}

/** POST .../{id}/preview — how many devices would change + a representative diff. */
export interface SiteActionPreview {
  action_id: string;
  scope: "site";
  changed_count: number;
  total_supported: number;
  sample_diff: ConfigActionDiffEntry[];
  /** Devices the apply will NOT push, with the reason (never silently dropped). */
  excluded_count?: number;
  excluded_devices?: SiteActionExcludedDevice[];
}

/** POST .../{id}/apply — aggregate fan-out result. */
export interface SiteActionApplyResult {
  action_id: string;
  scope: "site";
  devices: number;
  changed: number;
  pushed: number;
  skipped: number;
  errors: number;
  /** Devices refused by policy (never pushed), with the reason. */
  excluded?: number;
  excluded_devices?: SiteActionExcludedDevice[];
}

/* ------------------------------------------------------------------ *
 * WAN watchdog — the site's gateway connectivity-remediation policy
 * ------------------------------------------------------------------ */

/**
 * One rung of the escalation ladder. The ORDER is the policy: the firmware runs
 * these in sequence, one rung per `escalate_every` further failed checks, so the
 * UI must present them as an ordered ladder and never as an unordered checklist.
 * `watchdog-reset` stops petting the hardware watchdog -> a real reboot.
 */
export type WatchdogAction =
  | "dhcp-renew"
  | "network-restart"
  | "watchdog-reset"
  | "wifi"
  | "leds";

/** The policy as it currently sits in one gateway's live configuration. */
export interface WatchdogPolicy {
  ping_hosts: string[];
  download_hosts: string[];
  check_interval: number | null;
  check_threshold: number | null;
  actions: WatchdogAction[];
  escalate_every: string | number | null;
  reset_budget: string | number | null;
  reset_window: string | number | null;
  /** The single fact an operator most wants at a glance: can this reboot the box? */
  resets_on_failure: boolean;
}

/** Per-gateway applied state. `policy` is null when the gateway has none. */
export interface WatchdogGateway {
  serial: string;
  name: string;
  owgw_available: boolean;
  policy: WatchdogPolicy | null;
}

/** GET /sites/{id}/wan-watchdog */
export interface SiteWanWatchdog {
  site_id: number;
  gateways: WatchdogGateway[];
  /** Ladder in escalation order — render in this order, it is meaningful. */
  ladder: WatchdogAction[];
  presentation_actions: WatchdogAction[];
}

/** The body PUT to /sites/{id}/wan-watchdog. */
export interface WatchdogPolicyInput {
  ping_hosts: string[];
  download_hosts: string[];
  check_interval: number;
  check_threshold: number;
  actions: WatchdogAction[];
  escalate_every: number;
  reset_budget: number;
  reset_window: number;
}

/** PUT/DELETE result — aggregate fan-out across the site's gateways. */
export interface WatchdogApplyResult {
  site_id: number;
  gateways: number;
  changed: number;
  pushed: number;
  skipped: number;
  errors: number;
  policy?: WatchdogPolicyInput;
}

/* ------------------------------------------------------------------ *
 * MSP portal — the Customers (organizations) landing feed
 * ------------------------------------------------------------------ */

/** One customer (org) row on the MSP landing, with fleet rollups. */
export interface Customer {
  id: string;
  name: string;
  slug?: string | null;
  is_quarantine?: boolean;
  sites_count: number;
  devices_total: number;
  devices_online: number;
  devices_offline: number;
  alerts_open: number;
  /** Open CRITICAL alerts (OP#1854, additive; absent on older controllers). */
  alerts_critical?: number;
  /** Coarse roll-up: critical (any open critical) / warn (open alert or offline) / healthy. */
  health: "healthy" | "warn" | "critical";
}

/* ------------------------------------------------------------------ *
 * Customer Overview -- one row per site (GET /api/v2/sites/overview)
 * ------------------------------------------------------------------ */

/** Per-site firmware verdict. "off_baseline" is NOT "upgrade available": the backend never
 *  orders version strings, so a device that differs from its approved build may be newer. */
export type SiteFirmwareStatus = "current" | "off_baseline" | "unknown" | "no_baseline" | "none";

/** One site row of the Customer Overview table (the Meraki "Networks" table equivalent). */
export interface SiteOverviewRow {
  site_id: string;
  site_name: string;
  location?: string | null;
  org_id: string;
  org_name: string;
  devices_total: number;
  devices_online: number;
  /** Devices whose state is not ONLINE. */
  devices_offline: number;
  /** Clients connected NOW (services/client_counts); an offline device contributes 0. */
  clients_now: number;
  alerts_open: number;
  alerts_critical: number;
  /** Current online ratio 0-100; null when the site has no devices. */
  online_pct: number | null;
  /** Same ladder as Customers / Sites: critical > warn (alert or offline) > healthy. */
  health: "healthy" | "warn" | "critical";
  firmware: {
    status: SiteFirmwareStatus;
    matches: number;
    differs: number;
    no_baseline: number;
    unknown: number;
  };
}

export interface SitesOverviewResponse {
  generated_at: string;
  items: SiteOverviewRow[];
  count: number;
  total: number;
  totals: {
    sites: number;
    customers: number;
    devices: number;
    offline: number;
    clients_now: number;
    alerts_open: number;
  };
}

/* ------------------------------------------------------------------ *
 * Device-kind summary (the org "Overview" cards)
 * ------------------------------------------------------------------ */

/** One device-kind bucket in the per-kind rollup. */
export interface DeviceKindSummary {
  kind: string;
  label: string;
  online: number;
  offline: number;
  total: number;
}

/** GET /api/v2/device-summary */
export interface DeviceSummary {
  items: DeviceKindSummary[];
  total: number;
  online: number;
}

/* ------------------------------------------------------------------ *
 * SSIDs (Wireless > SSIDs) — site-scoped, PSK write-only
 * ------------------------------------------------------------------ */

export type SsidSecurity =
  | "open"
  | "wpa2"
  | "wpa3"
  | "wpa2/3"
  | "wpa2-enterprise"
  | "wpa3-enterprise";

/** One SSID as returned by the API (never carries the PSK). */
/** One broadcast window: the SSID is ACTIVE on these days between start and end (HH:MM).
 *  start > end wraps past midnight (e.g. 22:00-06:00). */
export interface SsidScheduleWindow {
  days: string[]; // "mon".."sun"
  start: string; // "HH:MM"
  end: string; // "HH:MM"
}

/** SSID broadcast schedule (UniFi schedule / Ruckus wlan-schedule parity).
 *  null/absent, or enabled=false, means "always on". */
export interface SsidSchedule {
  enabled: boolean;
  windows: SsidScheduleWindow[];
  tz?: string;
}

export type NotificationKind = "email" | "webhook" | "slack";

/**
 * A place a tenant's alerts are delivered (email / webhook / slack).
 *
 * OP#1917: the target is WRITE-ONLY. A webhook/Slack URL is the credential, so the server never
 * returns it: reads carry `target_masked` (display only, e.g. "https://hooks.example/…Xy9Z") and
 * `has_target`. Never send `target_masked` back as `target` (the server 400s it); omit `target`
 * on an update to keep the stored one.
 */
export interface NotificationChannel {
  id: number;
  org_id: number;
  kind: NotificationKind;
  name: string;
  target_masked: string;
  has_target: boolean;
  enabled: boolean;
  last_ok_at: string | null;
  last_error: string | null;
  created_at: string | null;
}

/** GET /api/v2/dashboard/overview — one aggregate the realtime Health screen polls. */
export interface DashboardOverview {
  generated_at: string;
  devices: {
    total: number;
    online: number;
    offline: number;
    by_kind: { kind: string; label: string; online: number; offline: number; total: number }[];
  };
  clients: { count: number };
  alerts: { critical: number; warn: number; total: number };
  health: { score: number | null; tone: "healthy" | "warn" | "critical" | "unknown" };
  top_aps: { serial: string; name: string; clients: number }[];
  /** What these counts cover (OP#1787, api/v2/dashboard.tile_scope). Optional: an older
   *  controller omits it and the tiles then show no scope line rather than a guessed one. */
  scope?: TileScope;
  activity: {
    window_seconds: number;
    bucket_seconds: number;
    series: { t: string; rx_bps: number; tx_bps: number; clients: number }[];
    now: { rx_bps: number; tx_bps: number; clients: number };
  };
}

export interface Ssid {
  name: string;
  bands: string[];
  security: SsidSecurity;
  hidden: boolean;
  /** Broadcast schedule (null = always on). Enforced controller-side by timed enable/disable. */
  schedule?: SsidSchedule | null;
  /** PMF / 802.11w mode: "disabled" | "optional" | "required". */
  pmf?: string;
  /** Per-SSID client cap (null = unlimited). */
  max_clients?: number | null;
  /** Whether a passphrase is set (the value itself is never returned). */
  has_passphrase: boolean;
  /** VLAN tag (null = untagged / on the uplink). */
  vlan: number | null;
  /** Client isolation (block station-to-station on the SSID). */
  isolate: boolean;
  /** Multicast-to-unicast conversion (airtime fix). Default ON. */
  unicast_conversion: boolean;
  /** Rate limits in Mbps (null = unlimited). down = to client, up = from client. */
  rate_down: number | null;
  rate_up: number | null;
  /** RADIUS (enterprise) auth server; the shared secret is never returned. */
  radius_host?: string | null;
  radius_port?: number | null;
  has_radius_secret?: boolean;
  /** Optional RADIUS accounting server (secret never returned). */
  radius_acct_host?: string | null;
  radius_acct_port?: number | null;
  has_radius_acct_secret?: boolean;
  /** False when the SSID is defined but not currently broadcast (disabled). */
  enabled: boolean;
  /** Broadcast target: "all" APs, or a tag/group name. */
  target: string;
  /** Operator wording of the target: "All APs in site" or "Group: <tag>" (OP#1778). */
  target_label?: string;
  /** How many of the site's readable APs carry it today (an "all" SSID on 3 of 10 is
   *  spread to the other 7 by its next save, which the save warns about). */
  coverage?: { on: number; of: number };
  on_devices: string[];
}

/** One AP in an SSID save preview (OP#1778). */
export interface SsidImpactAp {
  serial: string;
  name: string;
  /** owgw has no session: the push skips it now and it keeps its old state. */
  offline: boolean;
}

/** What a save does to each AP: the add/remove preview from validate or a 409. */
export interface SsidImpact {
  target: string;
  target_label: string;
  /** False for a brand-new SSID (its additions are not a warning). */
  exists: boolean;
  added: SsidImpactAp[];
  removed: SsidImpactAp[];
  /** Subset of `removed` for which this is the ONLY SSID: they broadcast nothing after. */
  left_without_ssids: SsidImpactAp[];
  /** APs whose config owgw could not return (not judged). */
  unknown: number;
  /** Drift repair side effect (OP#1814): APs running a generation the controller did not
   *  send whose held configuration this save ALSO re-sends (a push each). */
  also_repushed?: SsidRepushAp[];
  /** Diverged APs the re-push guard will NOT re-send to, with the reason. */
  repush_blocked?: SsidRepushAp[];
}

/** An AP in the drift-repair preview: the foreign generation it runs and why. */
export interface SsidRepushAp extends SsidImpactAp {
  generation: number;
  reason: string;
}

/** An AP site SSID operations skip (GET /sites/{id}/ssids `excluded_devices`). */
export interface SsidExcludedDevice {
  serial: string;
  name: string;
  /** manual | firmware | rejections, or "resync_pending" (eligible, current SSIDs owed). */
  source: ConfigCompatSource | "resync_pending" | null;
  label: string | null;
  reason: string | null;
}

/** GET /api/v2/sites/{id}/ssids */
export interface SsidList {
  site_id: number;
  owgw_available: boolean;
  ssids: Ssid[];
  /** APs not managed by site SSID operations (shown apart, never as failures). */
  excluded_devices?: SsidExcludedDevice[];
}

/** Fan-out result from create/update/delete. */
export interface SsidFanoutResult {
  devices: number;
  changed: number;
  pushed: number;
  skipped: number;
  errors: number;
  /**
   * APs whose push the controller stopped waiting for, because the fan-out hit its time
   * budget. These are NOT failures and NOT successes — the push may still be applying. The
   * UI must say so rather than report the change as complete, which is the mistake that let
   * a half-renamed fleet look like a finished rename (2026-09-11).
   */
  unresolved?: number;
  /**
   * APs the fan-out never contacted at all — queued behind the budget and cancelled. Unlike
   * `unresolved` these are KNOWN not to have changed, and must not be described as "still
   * applying": that sends an operator looking for a change that never happened.
   */
  not_attempted?: number;
  /** Pushes that landed but could not be verified before the budget ran out. */
  unverified?: number;
  /**
   * Pushes owgw ACCEPTED that the AP had not confirmed yet — "still applying", never a
   * failure. The EAP105 on 2026-09-14 was exactly this, and was mis-reported as failed.
   */
  pending?: number;
  /** One row per device: what happened on THAT AP. Never carries config or secrets. */
  results?: SsidPushRow[];
  /** Counts across `results`. `applied` includes APs already done in an earlier pass. */
  summary?: SsidPushSummary;
}

/**
 * Per-device push outcome. `already_applied`/`in_sync` count as applied (earlier pass or
 * nothing to change); `applied_with_warnings` counts as applied but the AP dropped parts it
 * does not support (uCentral error 1 WITH a `rejected` list); `rejected` counts as failed -- the
 * AP REFUSED the config and kept its old one (uCentral error 1 with no `rejected` list, or >= 2;
 * see services/configure_result.py); `diverged` counts as failed (AP runs a generation nobody
 * sent); `pending`/`unresolved` are still applying; `not_attempted` was never contacted;
 * `excluded` is a config-incompatible AP that was NOT sent the change (services/config_compat:
 * "Not managed: ... reflash needed") -- counted apart, never a failure, not in `total`;
 * `skipped`/`not_applicable` are excluded from the summary.
 */
export type SsidPushOutcome =
  | "applied"
  | "applied_with_warnings"
  | "rejected"
  | "diverged"
  | "already_applied"
  | "in_sync"
  | "pending"
  | "unresolved"
  | "failed"
  | "not_attempted"
  | "excluded"
  | "skipped"
  | "not_applicable";

export interface SsidPushRow {
  serial: string;
  /** Hostname when known, else the serial. */
  name: string;
  outcome: SsidPushOutcome;
  /** Human reason (owgw's text for failures, "waiting for the AP…" for pending). */
  reason: string | null;
  /** owgw command UUID, when one is known. */
  command: string | null;
}

export interface SsidPushSummary {
  total: number;
  applied: number;
  applied_now: number;
  already: number;
  pending: number;
  failed: number;
  not_reached: number;
  /** Config-incompatible APs not sent the change. Reported apart; NOT part of `total`. */
  excluded?: number;
}

/** A consequence the operator must acknowledge before saving (backend is authoritative). */
export interface SsidWarning {
  code: string;
  message: string;
  /** "hard" = an AP would broadcast NO SSID; needs its own explicit acknowledgement. */
  severity?: "warning" | "hard" | "info";
  /** band_unsupported only: the band some targeted APs lack ("6G"). */
  band?: string;
  /** band_unsupported only: names of the APs without that band. */
  devices?: string[];
}

/** POST /api/v2/sites/{id}/ssids/validate — dry run of the save, no writes. */
export interface SsidValidation {
  errors: string[];
  warnings: SsidWarning[];
  /** Per-AP add/remove preview (null when it could not be computed). */
  impact?: SsidImpact | null;
  /** OP#1818: the VLAN path from the targeted APs to the gateways (compact), or null. */
  path_check?: import("@/lib/vlanPath").VlanPathCompact | null;
  /**
   * INFO notes, never acknowledged (owner decision 2026-10-14): `vlan_path_unknown` -- a hop
   * that cannot be checked (e.g. a SonicFi switch until OP#1820). They never hold the save.
   */
  notes?: SsidWarning[];
}

/** PUT /devices/{serial}/tags and the bulk endpoint: group-targeted SSIDs a tag change moves. */
export interface TagSsidChanges {
  add: string[];
  remove: string[];
}

/** POST /devices/{serial}/ssids/apply-targets plan (dry run or the pushed result's `plan`). */
export interface ApplyTargetsPlan {
  serial: string;
  add: string[];
  remove: string[];
  left_without_ssids: boolean;
  /** Group SSIDs the AP should carry but no AP holds a definition for (cannot be added). */
  missing_definitions: string[];
}

/** GET /api/v2/sites/{id}/ssids/push-status — follow-up for pending pushes. */
export interface SsidPushStatus {
  site_id: number;
  results: SsidPushRow[];
  summary: SsidPushSummary;
}

/* ------------------------------------------------------------------ *
 * Switch ports (live SNMP) — Switch > Ports
 * ------------------------------------------------------------------ */

export interface SwitchPortLive {
  /** SNMP rows only; owgw-state rows have no ifindex. */
  ifindex?: number;
  /** SNMP: the ifName. owgw-state rows carry `port` instead (see normalizeLivePort). */
  name?: string;
  /** owgw-state (services/switch_state): the uCentral port name, e.g. "Ethernet0". */
  port?: string;
  /** owgw-state: link carrier from link-state; null when not reported. */
  carrier?: boolean | null;
  descr?: string | null;
  alias?: string | null;
  status?: "up" | "down";
  speed_mbps?: number | null;
  neighbor?: string | null;
}

/** GET /api/v2/devices/{serial}/switch/ports */
export interface SwitchPortsResponse {
  serial: string;
  /** False when SNMP is unconfigured/unreachable (e.g. disabled on the switch). */
  snmp_ok: boolean;
  ports: SwitchPortLive[];
  /** "owgw-state" | "snmp" | "none" (nothing polled: see `reason`). */
  source?: string;
  /** "offline": the switch is known offline / never seen, so the server polled nothing (OP#1860). */
  reason?: string | null;
  /** With reason "offline": the last contact (naive-UTC ISO), null when never seen. */
  offline_since?: string | null;
}

/** A device the Onboard screen can add to a site: unassigned, or placed elsewhere. */
export interface AssignableDevice extends Device {
  unassigned: boolean;
  current_site: string | null;
  current_customer: string;
  /** True when the device currently sits in the quarantine holding area (2026-10-19). */
  current_customer_is_quarantine?: boolean;
}

/* ------------------------------------------------------------------ *
 * Radio (RF) settings — Wireless > Radio Settings (site-scoped)
 * ------------------------------------------------------------------ */

export interface Radio {
  band: string;
  /** Specific channel number, or "auto" (RRM). */
  channel: number | "auto";
  channel_width: number | null;
  channel_mode?: string | null;
  /** TX power in dBm, or "auto". */
  tx_power: number | "auto";
  /** Multicast base rate in schema units (e.g. 24000 = 24 Mbps); null = radio default. */
  multicast_rate?: number | null;
  /** Channel plan: the channels auto-select may pick from (null = no restriction). */
  allowed_channels?: number[] | null;
  /** Whether DFS channels are permitted in the plan. */
  allow_dfs?: boolean | null;
  country?: string | null;
  on_devices: string[];
  /** What the site last SAVED for the band (null fields = never set). OP#1760. */
  site_setting?: { channel: number | "auto" | null; channel_width: number | null; tx_power: number | "auto" | null };
  /** APs with their own per-AP override on this band; a site save leaves those fields alone. */
  overridden_aps?: { serial: string; name: string; fields: string[] }[];
}

export interface RadioList {
  site_id: number;
  owgw_available: boolean;
  radios: Radio[];
}

export interface RadioFanoutResult {
  band: string;
  devices: number;
  changed: number;
  pushed: number;
  skipped: number;
  errors: number;
  /** APs whose own (overridden) fields this save left alone. OP#1760. */
  kept_overrides?: { serial: string; name: string; fields: string[] }[];
  /**
   * APs that did not answer inside the controller's owgw write timeout: owgw has the push and
   * Meridian confirms it in the background. Not errors, never re-sent (OP#1775 follow-up).
   */
  pending_confirmation?: { serial: string; name: string; reason: string; command_uuid: string | null }[];
}

/** A tag (group) in a site and the APs carrying it. */
export interface SiteTag {
  tag: string;
  count: number;
  serials: string[];
}

/** A device pre-registration: a MAC/serial reserved to a site, auto-claimed on arrival. */
export interface Prereg {
  id: number;
  org_id: number;
  site_id: number;
  identifier: string;      // normalized (alnum-lowercase) match key
  raw: string | null;      // what the operator typed
  note: string | null;
  created_at: string | null;
  created_by: string | null;
}

/* ------------------------------------------------------------------ *
 * Hospitality — Guest / captive Portal (site-scoped, per SSID)
 * ------------------------------------------------------------------ */

/** Splash-page branding for the captive portal (rendered into the uCentral web-root). */
export interface PortalBranding {
  headline?: string;
  subhead?: string;
  outer_bg?: string;
  card_bg?: string;
  headline_color?: string;
  text_color?: string;
  button_color?: string;
  logo?: string;
  terms_text?: string;
  redirect_url?: string;
}

export interface Portal {
  ssid: string;
  enabled: boolean;
  /** "click" (click-through) | "radius". */
  auth_mode: string | null;
  walled_garden: string[];
  radius_host?: string | null;
  radius_port?: number | null;
  has_radius_secret?: boolean;
  /** Round-tripped from the web-root sidecar; null when the AP has no Meridian branding. */
  branding?: PortalBranding | null;
  idle_timeout?: number | null;
  session_timeout?: number | null;
  on_devices: string[];
}

export interface PortalList {
  site_id: number;
  owgw_available: boolean;
  portals: Portal[];
}

export interface PortalFanoutResult {
  devices: number;
  changed: number;
  pushed: number;
  skipped: number;
  errors: number;
}

/* ------------------------------------------------------------------ *
 * Hospitality — Passpoint / Hotspot 2.0 (802.11u, per SSID)
 * ------------------------------------------------------------------ */

/** A NAI realm plus the EAP method a client uses to match it. `method` may be null when a
 *  stored realm was authored outside Meridian and its method couldn't be inferred. */
export interface NaiRealm {
  realm: string;
  method: "eap-tls" | "eap-ttls-pap" | "eap-peap-mschapv2" | string | null;
}

export interface Passpoint {
  ssid: string;
  /** True when the SSID is enterprise (802.1X) — real Passpoint auth needs this. */
  enterprise: boolean;
  enabled: boolean;
  venue_name: string | null;
  venue_group: number | null;
  venue_type: number | null;
  domain_name: string[];
  /** Roaming consortium OIs (hexstrings). */
  roaming_consortium: string[];
  /** NAI realms with the EAP method a client uses to match them (Passpoint discovery). */
  nai_realm: NaiRealm[];
  friendly_name: string | null;
  /** ISO-639-2 language of the venue/operator names (rendered as "eng:<name>"). */
  venue_language?: string;
  venue_url?: string | null;
  internet: boolean;
  on_devices: string[];
  /** Per-AP state (OP#1776): the page used to read only the first AP. */
  devices?: PasspointDevice[];
  /** enabled = every AP carrying the SSID has the block; partial = only some. */
  state?: "enabled" | "disabled" | "partial";
  enabled_count?: number;
  device_count?: number;
  /** APs where the block sits on a NON-enterprise SSID: their radio is rejected (dark). */
  invalid_devices?: string[];
}

export interface PasspointDevice {
  serial: string;
  name: string;
  enabled: boolean;
  enterprise: boolean;
}

export interface PasspointList {
  site_id: number;
  owgw_available: boolean;
  passpoint: Passpoint[];
}

/* ------------------------------------------------------------------ *
 * Hospitality — Guest Vouchers (site-scoped access codes)
 * ------------------------------------------------------------------ */

export interface Voucher {
  code: string;
  /** "active" | "revoked" | "expired". */
  state: string;
  batch: string | null;
  label: string | null;
  duration_minutes: number | null;
  data_cap_mb: number | null;
  created_at: string | null;
  expires_at: string | null;
  usable: boolean;
}

export interface VoucherList {
  site_id: number;
  total: number;
  active: number;
  vouchers: Voucher[];
}

/** Result of a generate call: the new codes + the captive reconcile push counts. */
export interface VoucherBatchResult {
  batch: string;
  count: number;
  vouchers: Voucher[];
  push: PortalFanoutResult & { note?: string };
}

/* ------------------------------------------------------------------ *
 * Multi-PSK / IPSK — per-SSID pre-shared keys (MAC + VLAN bound)
 * ------------------------------------------------------------------ */

export interface Psk {
  /** Store row id (used to remove it). */
  id: number;
  /** Operator-facing name, e.g. "Finance-Pass" / "Room 214". */
  label: string | null;
  /** Bound device MAC, or null for a wildcard (any device). */
  mac: string | null;
  /** Per-key VLAN, or null. */
  vlan_id: number | null;
  /** "active" | "revoked". */
  state: string;
  /** The passphrase itself is never returned on read. */
  has_key: boolean;
  created_at: string | null;
}

export interface PskList {
  site_id: number;
  ssid: string;
  owgw_available: boolean;
  /** False when the selected SSID is open/enterprise (multi-PSK does not apply). */
  is_psk_ssid: boolean;
  psks: Psk[];
}

/* ------------------------------------------------------------------ *
 * RADIUS Servers registry (BYO — the client's own RADIUS)
 * ------------------------------------------------------------------ */

export interface RadiusServer {
  id: number;
  name: string;
  /** "external" (BYO) | "managed" (platform FreeRADIUS keystone). */
  mode?: string;
  auth_host: string;
  auth_port: number;
  acct_host: string | null;
  acct_port: number | null;
  /** The shared secret is write-only; only its presence is reported. */
  has_secret: boolean;
}

export interface RadiusServerList {
  radius_servers: RadiusServer[];
}

/* ------------------------------------------------------------------ *
 * Managed RADIUS — realms + credentials (the Passpoint keystone)
 * ------------------------------------------------------------------ */

export type EapMethod = "eap-tls" | "eap-ttls-pap" | "eap-peap-mschapv2";

export interface RadiusRealm {
  id: number;
  org_id: number;
  /** NAI realm / domain, e.g. "guest.hotel.example". */
  realm: string;
  label: string | null;
  default_method: EapMethod | string;
  enabled: boolean;
}

export interface RadiusCredential {
  id: number;
  realm_id: number;
  username: string;
  method: EapMethod | string;
  /** Dynamic VLAN returned on auth (Tunnel-Private-Group-Id), or null. */
  vlan_id: number | null;
  state: "active" | "revoked" | "expired" | string;
  /** Per-guest checkout time (ISO-8601), or null for no expiry. */
  expires_at: string | null;
  source: string;
  issued_for: string | null;
  last_auth_at: string | null;
  /** One-time inner password — present ONLY in an issue/provision response, never on list. */
  password?: string;
}

/** Result of POST /radius/realms/{id}/provision — issues a credential AND returns an
 *  installable profile (the one-time password is embedded in `profile`). */
export interface ProvisionResult {
  credential: RadiusCredential;
  profile: string;
  platform: "ios" | "android" | string;
  content_type: string;
  filename: string;
}

/** Result of adding/generating a PSK — the plaintext key is returned exactly ONCE. */
export interface PskAddResult {
  id: number;
  key: string;
  label: string | null;
  mac: string | null;
  vlan_id: number | null;
  push: PortalFanoutResult & { note?: string };
}

/**
 * GET /api/v2/devices/status-counts — how many devices sit in each status bucket across the
 * WHOLE filtered scope, not just the page the list happens to have fetched.
 *
 * `total` is the size of that scope. It is not the sum of a page, and the tiles must not be
 * derived from one: the list is ordered `last_seen DESC NULLS LAST`, which sorts precisely
 * the offline and never-seen devices past the end of the first page.
 */
export interface FleetStatusCounts {
  counts: Record<DeviceStatus, number>;
  total: number;
}



/**
 * GET /api/v2/clients/usage — "who is using too much bandwidth".
 *
 * `avg_bps` is the honest number: rx/tx counters are CUMULATIVE SINCE ASSOCIATION, so raw
 * totals rank "who has been connected longest". Ranking is on the per-second average over each
 * client's own connected window. Clients below `min_connected_sec` are counted but never
 * ranked (`ratable: false`, `avg_bps: null`) — a 2-second burst would otherwise top the table.
 */
export interface ClientUsageRow {
  mac: string;
  hostname: string | null;
  ssid: string | null;
  band: string | null;
  device_serial: string | null;
  device_name: string | null;
  site_id: number | null;
  connected_sec: number;
  upload_bytes: number;     // AP received  == client upload
  download_bytes: number;   // AP sent      == client download
  total_bytes: number;
  avg_bps: number | null;
  ratable: boolean;
  over_threshold?: boolean;
}

export interface ClientUsageResponse {
  clients: number;
  ratable: number;
  ignored_too_brief: number;
  min_connected_sec: number;
  threshold_bps: number | null;
  over_threshold: number;
  totals: { upload_bytes: number; download_bytes: number };
  top: ClientUsageRow[];
  flagged: ClientUsageRow[];
}

/** GET /api/v2/firmware/compliance — device firmware against the APPROVED image per type. */
export interface FirmwareComplianceDevice {
  serial: string;
  name: string;
  kind: string;
  device_type: string;
  site_id: number | null;
  running: string | null;
  baseline: string | null;
  baseline_image_id: number | null;
  /** matches | differs | no_baseline | unknown — `differs` does NOT assert which is newer. */
  state: "matches" | "differs" | "no_baseline" | "unknown";
}

export interface FirmwareComplianceType {
  device_type: string;
  baseline: string | null;
  total: number;
  states: Record<string, number>;
  distinct_builds: number;
  builds: { version: string; count: number }[];
}

export interface FirmwareComplianceResponse {
  totals: {
    devices: number;
    matches: number;
    differs: number;
    no_baseline: number;
    unknown: number;
    distinct_builds: number;
  };
  by_device_type: FirmwareComplianceType[];
  devices: FirmwareComplianceDevice[];
}

/**
 * GET /api/v2/sites/{siteId}/gateway-routes — the Gateway > Route Table screen.
 *
 * Read LIVE over SSH per request (a uCentral gateway does not report its FIB in state), so
 * every gateway carries its own `error` instead of the whole response failing: one unreachable
 * box must not blank a healthy one's routes.
 */
export interface GatewayRoute {
  destination: string;        // "default" or a CIDR
  gateway: string | null;     // via ...
  interface: string | null;   // dev ...
  proto: string | null;
  scope: string | null;
  source: string | null;      // src ...
  metric: number | null;
  raw: string;                // the original line, shown on hover / for unparsed attributes
}

export interface GatewayRouteRule {
  priority: number;
  selector: string;           // "from all iif down5v1050"
  table: string;
}

export interface GatewayRouteTable {
  id: string;                 // "main" | "1" | ...
  label: string;              // "Main" | "down5v1050 (table 4)"
  rule: string | null;        // the policy rule that steers into this table
  interface: string | null;
  routes: GatewayRoute[];
}

export interface GatewayRoutesEntry {
  serial: string;
  name: string;
  role: "primary" | "secondary" | null;
  address?: string;
  credential_source?: string;
  fetched_at?: string;
  rules: GatewayRouteRule[];
  tables: GatewayRouteTable[];
  error?: { code: string; message: string };
}

export interface GatewayRoutesResponse {
  site_id: number;
  gateways: GatewayRoutesEntry[];
}

/* ------------------------------------------------------------------
 * Gateway Security Posture — the Gateway > Security Posture screen.
 *
 * GET /api/v2/sites/{siteId}/gateway-posture. A read-time AGGREGATION of the firewall, routes
 * and WAN-watchdog reads diffed against a small rule set (services/gateway_posture) — not an
 * IDS/event feed, which a uCentral whitebox gateway cannot produce. Each finding carries a
 * stable `code`; the UI keys wording and colour off `severity`, never the message text.
 * ------------------------------------------------------------------ */
export type PostureSeverity = "warn" | "info";

export interface PostureFinding {
  severity: PostureSeverity;
  code: string;               // isolation_off_vpn | no_blocked_destinations | empty_route_table | no_wan_watchdog
                              // + shasta_* / ha_dhcp_force_missing from services/shasta_cutoff (OP #1674)
  message: string;            // operator-facing, already says WHY it matters
}

export interface GatewayPostureEntry {
  serial: string;
  name: string;
  findings: PostureFinding[]; // severity-sorted by the backend (warn first)
}

export interface GatewayPostureResponse {
  site_id: number;
  gateways: GatewayPostureEntry[];
  summary: { warn_count: number; info_count: number };
}

/* ------------------------------------------------------------------
 * Operators — the people who can log in.
 *
 * GET/POST /api/v2/operators, PATCH/DELETE /api/v2/operators/<id>.
 * Identity lives in the IdP; Meridian stores no user rows. The screen
 * deliberately speaks in "operators", "role" and "buildings" — never in
 * identity-provider vocabulary, which the audience has no reason to learn.
 * ------------------------------------------------------------------ */

export interface Operator {
  id: string;
  username: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  enabled: boolean;
  /** Customers this operator belongs to. */
  org_ids: number[];
  /**
   * Buildings an operator is confined to. MEANINGFUL ONLY for read_only and
   * site_operator — org_admin and above span every site in their customer, and
   * the server ignores this for them. The UI must show that rather than
   * silently dropping the value.
   */
  site_ids: number[];
  roles: Role[];
  /**
   * Flagged as a test / service account (Keycloak group "meridian-test-accounts", owner
   * decision 2026-10-15). Never listed on a customer Team page; a platform admin sees it under
   * Platform staff with a "Test" badge. Display-only: it grants and removes nothing.
   * Absent on servers before this change (treat as false).
   */
  is_test?: boolean;
  /** When the account was created (sign-in service createdTimestamp, ISO UTC), or null. */
  created_at?: string | null;
  /**
   * Last sign-in and the hygiene flag (OP#1897), from the server's cached copy of the sign-in
   * service's login history. Absent on older servers; render with lib/signIn.signInView,
   * which reads absent as "unknown".
   */
  sign_in?: SignIn;
}

/**
 * What happened to someone's open sessions when their access was removed or reduced.
 * DELETE /operators/{id} always returns it. PATCH returns it, merged into the operator
 * record, only when the change took access away (`signed_out: true`).
 */
export interface OperatorSignOut {
  sessions_ended: number;
  /** "ok", or "failed: <reason>" when the sign-in service did not confirm the sign-out. */
  keycloak_logout: string;
  /** Plain-language warning when the sign-out was incomplete, else null. */
  warning: string | null;
}

export interface OperatorList {
  items: Operator[];
  count: number;
}

/**
 * Can operators be managed at all right now?
 *
 * `ready: false` is a real state, not an error: the identity provider may not be
 * configured, or it may not declare the attributes that carry site assignment —
 * in which case a created operator would look correct and be confined to
 * nothing. Creation must be blocked, with the reason on screen.
 */
export interface OperatorPreflight {
  configured: boolean;
  ready: boolean;
  /** Whether the identity provider can actually send mail. Without it, inviting someone
   *  would create an account they can never sign in to, so the UI must not offer it. */
  can_send_email?: boolean;
  /** Email invites can be offered. False until the GuestTek sender is configured server-side
   *  (OPERATOR_INVITE_EMAIL_ENABLED); the option is then shown disabled with the reason. */
  invite_email_available?: boolean;
  /** Plain-language reason the invite option is disabled, or null when available. */
  invite_email_reason?: string | null;
  /** Realm password policy, mirrored by the temporary-password generator and checklist. */
  password_policy?: PasswordPolicy;
  /** Profile fields every new account must have, read from the realm's User Profile
   *  (today: email, first_name, last_name). Missing on older servers: the dialog then
   *  requires all three (lib/teamMember REQUIRED_FALLBACK). */
  required_fields?: Array<"email" | "first_name" | "last_name">;
  /** "realm" when read from the User Profile, "fallback" when it could not be. */
  required_fields_source?: "realm" | "fallback";
  /** Other attributes the realm requires that this dialog does not collect. The person will
   *  be asked for these by the sign-in service at first sign-in. */
  unsupported_required_attributes?: string[];
  detail: string;
  missing_attributes?: string[];
  assignable_roles?: Role[];
}

/* ------------------------------------------------------------------
 * Device import — names and PLACEMENT from the install workbook.
 *
 * POST /api/v2/devices/import. Placement is the reason this exists: the
 * naming template's {loc} token has no other source, because nothing
 * reaching the controller at check-in knows which closet or room a device
 * sits in.
 * ------------------------------------------------------------------ */

/** What one workbook row would do. `unknown` = no such device in the caller's scope. */
export type ImportOutcome = "update" | "unchanged" | "unknown";

export interface ImportChange {
  from: string | null;
  to: string;
}

export interface ImportItem {
  serial: string;
  name: string | null;
  location: string | null;
  outcome: ImportOutcome;
  /** Keyed by device field — `hostname` and/or `location`. Empty when nothing differs. */
  changes: Record<string, ImportChange>;
  site_id?: number | null;
}

export interface ImportSummary {
  rows: number;
  update: number;
  unchanged: number;
  unknown: number;
}

export interface ImportResult {
  applied: boolean;
  summary: ImportSummary;
  items: ImportItem[];
  changed?: string[];
}

/* ------------------------------------------------------------------
 * Network users — the people who CONNECT, not the people who administer.
 *
 * The other half of the Meraki split. Organization > Administrators is
 * `Operator` above; this is Organization > Users. Two different
 * populations with two different lifecycles: a resident is not an
 * operator with fewer buttons.
 *
 * The shape that matters is `grants`. A network user is deliberately NOT
 * confined to one customer — one email can reach several — so where they
 * may connect is a LIST of (customer, buildings) rather than a pair of
 * columns, and `all_sites` is a distinct state from "every building
 * happens to be ticked" (it keeps covering buildings added later).
 * ------------------------------------------------------------------ */

export interface NetworkUserGrant {
  org_id: number;
  /** Every building in this customer, including ones added after the grant. */
  all_sites: boolean;
  /** Named buildings. Empty when all_sites is true. */
  site_ids: number[];
}

export interface NetworkUser {
  id: number;
  /** The identity, globally unique and lower-cased by the server. */
  email: string;
  display_name: string | null;
  /** "active" | "suspended". Suspension blocks them EVERYWHERE, not just here. */
  state: string;
  notes: string | null;
  created_at: string | null;
  /** Customers visible to the caller. Others are stripped server-side. */
  org_ids: number[];
  grants: NetworkUserGrant[];
  /**
   * False when this person also holds access the caller cannot see.
   *
   * Suspend and delete are not scoped actions — they hit every property at
   * once — so the server refuses them here. The UI disables those controls
   * and says why, rather than letting the click return a 403.
   */
  fully_visible: boolean;
}

export interface NetworkUserList {
  items: NetworkUser[];
  count: number;
  total: number;
  limit: number;
  offset: number;
}

/* ------------------------------------------------------------------
 * Gateway firewall — GET /api/v2/devices/{serial}/gateway/firewall.
 *
 * Scoped to what uCentral can enforce and nothing more. There is no
 * general outbound rule list on this platform; `capabilities` says so
 * explicitly so the screen states the ceiling rather than implying a
 * control the device would never receive.
 * ------------------------------------------------------------------ */

export interface GatewayPortForward {
  interface: string;
  protocol: "tcp" | "udp" | "any";
  /** A port ("443") or an inclusive range ("8000:8010"). */
  external_port: string;
  internal_port: string;
  /**
   * The real address of the device traffic is sent to — what a human reads.
   * Null when the network has no static subnet to resolve the offset against.
   */
  internal_address: string | null;
  /**
   * What the gateway actually stores: a wildcard masked into the interface
   * subnet ("0.0.0.120"), NOT an address. Surfaced alongside the resolved
   * form so a mismatch can be shown rather than silently resolved one way.
   */
  internal_address_offset: string;
  offset: number | null;
}

export interface GatewayFirewallNetwork {
  name: string;
  vlan: number | null;
  subnet: string | null;
  /** False for a DHCP/unaddressed leg: it can be isolated, but cannot host a forward. */
  addressable: boolean;
  isolate_hosts: boolean;
  blocked_destinations: string[];
  services: string[];
  port_forwards: GatewayPortForward[];
}

export interface GatewayFirewall {
  upstream: Array<{ name: string; services: string[] }>;
  networks: GatewayFirewallNetwork[];
  summary: {
    port_forwards: number;
    isolated_networks: number;
    networks_with_blocks: number;
  };
  capabilities: {
    port_forward: boolean;
    guest_isolation: boolean;
    blocked_destinations: boolean;
    outbound_rules: boolean;
    note: string;
  };
  config_uuid?: number | null;
}


/**
 * One row of GET /api/v2/orgs/{orgId}/sites.
 *
 * Distinct from `Site` above, which describes the session's own site list and carries
 * STRING ids. This endpoint returns numeric ids and, since 2026-09-25, a fleet roll-up per
 * site. The health ladder is deliberately identical to `Customer.health` — two screens
 * answering "what is wrong here" with two different definitions is how an operator learns
 * to distrust both.
 */
export interface SiteListRow {
  id: number;
  org_id: number;
  name: string;
  location?: string | null;
  devices_total?: number;
  devices_online?: number;
  devices_offline?: number;
  alerts_open?: number;
  health?: "healthy" | "warn" | "critical";
}
